import { useQuery } from '@tanstack/react-query';
import type {
  FeatureMatrix,
  Plan,
  Subscription,
  TenantAccountStats,
  TenantDetail,
} from '@up2d8/contracts';
import { analyseAll, totals, type BillingTotals, type TenantBilling } from '../../shared/billing';
import { useSession } from '../../shared/session';

/**
 * De lijsten die meerdere schermen van Master delen (MST-5, MST-6): organisaties, pakketten,
 * abonnementen, accountcijfers en de functiematrix. Allemaal onder de sleutel `platform`, zodat een
 * wijziging ergens in Master ze in één keer ververst.
 */

export function useAllTenants() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['platform', 'tenants-all'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/tenants', {
        params: { query: { limit: 100 } as never },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: TenantDetail[] }).items;
    },
  });
}

export function usePlans() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/plans');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: Plan[] }).items;
    },
  });
}

export function useSubscriptions() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['platform', 'subscriptions'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/subscriptions');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: Subscription[] }).items;
    },
  });
}

export function useAccountStats() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['platform', 'account-stats'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/accounts/stats');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: TenantAccountStats[] }).items;
    },
  });
}

export function useFeatureMatrix() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['platform', 'feature-matrix'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/features/matrix');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as FeatureMatrix;
    },
  });
}

/** Alles samen, en de analyse erover (MST-6). `rows` is leeg zolang er iets laadt. */
export function useBilling(): {
  pending: boolean;
  error: boolean;
  rows: TenantBilling[];
  /** Als `rows`, maar mét gearchiveerde organisaties: alleen voor de export. */
  allRows: TenantBilling[];
  totals: BillingTotals | null;
  plans: Plan[];
} {
  const tenants = useAllTenants();
  const plans = usePlans();
  const subscriptions = useSubscriptions();
  const stats = useAccountStats();
  const matrix = useFeatureMatrix();
  const all = [tenants, plans, subscriptions, stats, matrix];
  const pending = all.some((query) => query.isPending);
  const error = all.some((query) => query.isError);
  if (pending || error || !tenants.data || !plans.data) {
    return { pending, error, rows: [], allRows: [], totals: null, plans: plans.data ?? [] };
  }
  const allRows = analyseAll(
    tenants.data,
    plans.data,
    subscriptions.data ?? [],
    stats.data ?? [],
    matrix.data?.rows ?? [],
  );
  // Dezelfde regel als `analyse`: het archief telt niet mee in de cijfers.
  const rows = allRows.filter((row) => row.tenant.status !== 'archived');
  return { pending, error, rows, allRows, totals: totals(rows, plans.data), plans: plans.data };
}
